import { LoadingState } from '@sportik/ui/components/LoadingState';
import { noop, type QueryClient } from '@tanstack/react-query';
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  RouterContextProvider,
} from 'react-router';
import { FollowSignOut } from '@/components/auth/FollowSignOut';
import { RouteError } from '@/components/error/RouteError';
import { AppLayout } from '@/components/layout/AppLayout';
import { versionOptions } from '@/services/version';
import { clientLoader, tabLoader } from './client';
import { clientsLoader } from './clients';
import { queryClientContext } from './query-client-context';
import { requireSignIn, signInLoader } from './sign-in';

// Route objects only (ADR 0007). Pages are lazy children of the shell route,
// each module exporting the `Component` React Router spreads onto the route.
// A route's reads start in its loader, here, so the page's code and its data
// download side by side (ADR 0016).
export const routes: RouteObject[] = [
  {
    Component: FollowSignOut,
    // For the sign-in page and this wrapper; the shell has its own below.
    errorElement: <RouteError />,
    children: [
      {
        path: '/sign-in',
        loader: signInLoader,
        hydrateFallbackElement: <LoadingState />,
        lazy: () => import('@/pages/sign-in/SignInPage'),
      },
      {
        path: '/',
        // Before any page's loader: signed out goes to sign-in, signed in
        // signs in to the API.
        middleware: [requireSignIn],
        Component: AppLayout,
        // For the shell itself breaking or the API refusing the account; a
        // page fails one level down.
        errorElement: <RouteError />,
        // Shown on first load, while the account is prepared and the first
        // lazy page loads.
        hydrateFallbackElement: (
          <LoadingState label="Preparing your account…" />
        ),
        children: [
          {
            // Pathless: a page that fails renders its error in the shell's outlet,
            // so the navigation stays on screen.
            errorElement: <RouteError />,
            children: [
              { index: true, element: <Navigate to="/clients" replace /> },
              {
                path: 'clients',
                loader: clientsLoader,
                lazy: () => import('@/pages/clients/ClientsPage'),
              },
              {
                // The client page: a frame over six tabs, each a child route with
                // its own reads. Its `ErrorBoundary` comes with the lazy module:
                // a client that cannot be read fails here, inside the shell.
                path: 'clients/:clientId',
                loader: clientLoader,
                lazy: () => import('@/pages/client/ClientPage'),
                children: [
                  {
                    index: true,
                    loader: tabLoader('overview'),
                    lazy: () =>
                      import('@/pages/client-overview/ClientOverviewPage'),
                  },
                  {
                    path: 'program',
                    loader: tabLoader('program'),
                    lazy: () =>
                      import('@/pages/client-program/ClientProgramPage'),
                  },
                  {
                    path: 'statistics',
                    loader: tabLoader('statistics'),
                    lazy: () =>
                      import('@/pages/client-statistics/ClientStatisticsPage'),
                  },
                  {
                    path: 'body',
                    loader: tabLoader('body'),
                    lazy: () => import('@/pages/client-body/ClientBodyPage'),
                  },
                  {
                    path: 'activity',
                    loader: tabLoader('activity'),
                    lazy: () =>
                      import('@/pages/client-activity/ClientActivityPage'),
                  },
                  {
                    path: 'settings',
                    loader: tabLoader('settings'),
                    lazy: () =>
                      import('@/pages/client-settings/ClientSettingsPage'),
                  },
                ],
              },
              {
                path: 'communities',
                lazy: () => import('@/pages/placeholder/PlaceholderPage'),
              },
              {
                path: 'settings',
                // Not awaited: the page shows the read's own pending and error
                // states, so a failure here must not fail the navigation.
                loader: ({ context }) => {
                  void context
                    .get(queryClientContext)
                    .query(versionOptions())
                    .catch(noop);
                },
                lazy: () => import('@/pages/settings/SettingsPage'),
              },
              {
                path: '*',
                lazy: () => import('@/pages/not-found/NotFoundPage'),
              },
            ],
          },
        ],
      },
    ],
  },
];

export function routerContext(queryClient: QueryClient) {
  const context = new RouterContextProvider();
  context.set(queryClientContext, queryClient);
  return context;
}

export function createRouter(queryClient: QueryClient) {
  return createBrowserRouter(routes, {
    getContext: () => routerContext(queryClient),
  });
}
